"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault").default;
var _interopRequireWildcard = require("@babel/runtime/helpers/interopRequireWildcard").default;
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.DatePicker = void 0;
var _extends2 = _interopRequireDefault(require("@babel/runtime/helpers/extends"));
var _slicedToArray2 = _interopRequireDefault(require("@babel/runtime/helpers/slicedToArray"));
var _objectWithoutProperties2 = _interopRequireDefault(require("@babel/runtime/helpers/objectWithoutProperties"));
var _dateFns = require("date-fns");
var _propTypes = _interopRequireDefault(require("prop-types"));
var _react = _interopRequireWildcard(require("react"));
var _constants = require("../../constants");
var _dates = require("../../util/dates");
var _identifier = require("../../util/identifier");
var _useIsomorphicLayoutEffect = require("../../util/useIsomorphicLayoutEffect");
var _DatePickerButton = require("../DatePickerButton/DatePickerButton");
var _DatePickerSelect = require("../DatePickerSelect/DatePickerSelect");
var _Flex = require("../Flex/Flex");
var _Heading = require("../Heading/Heading");
var _index = require("../Icon/index");
var _Text = require("../Text/Text");
var _VisuallyHidden = require("../VisuallyHidden/VisuallyHidden");
var _DatePicker = require("./DatePicker.styles");
var _excluded = ["dateFormat", "defaultSelected", "disabledDates", "minDate", "maxDate", "selected", "onChange"];
/**
 * Follows [WAI-ARIA accessible](https://www.w3.org/TR/wai-aria-practices/examples/dialog-modal/datepicker-dialog.html)
 * specs.
 *
 * Date picker uses the [date-fns](https://date-fns.org/) library for parsing, formatting, and
 * general manipulation of dates.
 *
 * @subcomponents [`DatePickerInput`]
 * @since 8.0.0
 */
var DatePicker = /*#__PURE__*/(0, _react.forwardRef)(function (_ref, forwardedRef) {
  var dateFormat = _ref.dateFormat,
    defaultSelected = _ref.defaultSelected,
    disabledDates = _ref.disabledDates,
    minDate = _ref.minDate,
    maxDate = _ref.maxDate,
    selected = _ref.selected,
    onChange = _ref.onChange,
    rest = (0, _objectWithoutProperties2.default)(_ref, _excluded);
  /** *************
   * DATA PROCESS *
   ************** */

  var _useState = (0, _react.useState)((0, _dates.getParsedDates)(defaultSelected, dateFormat)),
    _useState2 = (0, _slicedToArray2.default)(_useState, 2),
    uncontrolledDate = _useState2[0],
    setUncontrolledDate = _useState2[1];
  var controlledDate = (0, _react.useMemo)(function () {
    return (0, _dates.getParsedDates)(selected, dateFormat);
  }, [dateFormat, selected]);
  var parsedSelectedDate = typeof selected !== 'undefined' ? controlledDate : uncontrolledDate;
  var parsedMinDate = (0, _react.useMemo)(function () {
    var date = (0, _dates.getParsedDates)(minDate, dateFormat);
    if ((0, _dateFns.isValid)(date)) {
      return date;
    }
    // Given min date is invalid, default to 5 years before
    if ((0, _dateFns.isValid)(parsedSelectedDate)) {
      date = parsedSelectedDate;
    } else {
      date = new Date();
    }
    return (0, _dateFns.addYears)(date, -5);
  }, [dateFormat, minDate, parsedSelectedDate]);
  var parsedMaxDate = (0, _react.useMemo)(function () {
    var date = (0, _dates.getParsedDates)(maxDate, dateFormat);
    if ((0, _dateFns.isValid)(date)) {
      return date;
    }
    // Given max date is invalid, default to 5 years after
    if ((0, _dateFns.isValid)(parsedSelectedDate)) {
      date = parsedSelectedDate;
    } else {
      date = new Date();
    }
    return (0, _dateFns.addYears)(date, 5);
  }, [dateFormat, maxDate, parsedSelectedDate]);
  var parsedDisabledDates = (0, _react.useMemo)(function () {
    return typeof disabledDates === 'function' ? disabledDates : (0, _dates.getParsedDates)(disabledDates, dateFormat);
  }, [dateFormat, disabledDates]);

  // if selected date is a disabled date, set initial focus on the first available date
  var initialFocusDate = (0, _react.useMemo)(function () {
    return (0, _dates.getFirstAvailableDate)(parsedSelectedDate, parsedDisabledDates, parsedMinDate, parsedMaxDate);
  }, [parsedDisabledDates, parsedMaxDate, parsedMinDate, parsedSelectedDate]);
  var ariaLabelledby = (0, _react.useRef)((0, _identifier.identifier)());
  var _useState3 = (0, _react.useState)(initialFocusDate),
    _useState4 = (0, _slicedToArray2.default)(_useState3, 2),
    focusDate = _useState4[0],
    setFocusDate = _useState4[1];
  var _useState5 = (0, _react.useState)(false),
    _useState6 = (0, _slicedToArray2.default)(_useState5, 2),
    dayFocusable = _useState6[0],
    setDayFocusable = _useState6[1];
  var month = (0, _react.useMemo)(function () {
    return (0, _dateFns.getMonth)(focusDate);
  }, [focusDate]);
  var minYear = (0, _react.useMemo)(function () {
    return (0, _dateFns.getYear)(parsedMinDate);
  }, [parsedMinDate]);
  var maxYear = (0, _react.useMemo)(function () {
    return (0, _dateFns.getYear)(parsedMaxDate);
  }, [parsedMaxDate]);
  var year = (0, _react.useMemo)(function () {
    return (0, _dates.getValidYear)(focusDate, minYear, maxYear);
  }, [focusDate, maxYear, minYear]);
  var monthMatrix = (0, _react.useMemo)(function () {
    return (0, _dates.getMonthMatrix)(month, year);
  }, [month, year]);
  var yearRange = (0, _react.useMemo)(function () {
    return (0, _dates.getArrayFromRange)(minYear, maxYear, 1);
  }, [maxYear, minYear]);
  var isNextMonthDisabled = (0, _react.useMemo)(function () {
    return year === maxYear && month >= (0, _dateFns.getMonth)(parsedMaxDate);
  }, [year, month, maxYear, parsedMaxDate]);
  var isPrevMonthDisabled = (0, _react.useMemo)(function () {
    return year === minYear && month <= (0, _dateFns.getMonth)(parsedMinDate);
  }, [year, month, minYear, parsedMinDate]);

  /** ********
   * ACTIONS *
   ********* */

  // handle focus ring on keyboard events
  (0, _react.useEffect)(function () {
    if (forwardedRef && forwardedRef.current && dayFocusable) {
      forwardedRef.current.focus();
    }
  }, [dayFocusable, focusDate, forwardedRef]);
  var updateFocusDate = (0, _react.useCallback)(function (newDate, isDayFocusable, isPageKeyCode) {
    if (isDayFocusable === void 0) {
      isDayFocusable = true;
    }
    if (isPageKeyCode === void 0) {
      isPageKeyCode = false;
    }
    // keyboard day events and new date is out of range, ignore
    if (isDayFocusable && !(isPageKeyCode || (0, _dates.isDateInBetween)(newDate, parsedMinDate, parsedMaxDate))) {
      return;
    }
    var date = newDate;
    // mouse events or keyboard page events and new date is disabled
    // find the first available day
    if (!isDayFocusable || isPageKeyCode) {
      date = (0, _dates.getFirstAvailableDate)(date, parsedDisabledDates, parsedMinDate, parsedMaxDate);
    }
    setFocusDate(date);
    setDayFocusable(isDayFocusable);
  }, [parsedDisabledDates, parsedMaxDate, parsedMinDate]);
  var updateSelectedDate = (0, _react.useCallback)(function (date, meta) {
    setUncontrolledDate(date);
    if (onChange) {
      onChange({
        date: date,
        formatted: (0, _dateFns.format)(date, dateFormat)
      }, meta);
    }
  }, [dateFormat, onChange]);
  (0, _useIsomorphicLayoutEffect.useIsomorphicLayoutEffect)(function () {
    updateFocusDate(initialFocusDate, false);
  }, [initialFocusDate, updateFocusDate]);

  /** *********
   * BEHAVIOR *
   ********** */

  var onPrevMonthCallback = (0, _react.useCallback)(function () {
    var newDate = (0, _dateFns.addMonths)(focusDate, -1);
    updateFocusDate(newDate, false);
  }, [focusDate, updateFocusDate]);
  var onNextMonthCallback = (0, _react.useCallback)(function () {
    var newDate = (0, _dateFns.addMonths)(focusDate, 1);
    updateFocusDate(newDate, false);
  }, [focusDate, updateFocusDate]);
  var onYearChangeCallback = (0, _react.useCallback)(function (e) {
    var newDate = (0, _dateFns.setYear)(focusDate, Number(e.target.value));
    updateFocusDate(newDate, false);
  }, [focusDate, updateFocusDate]);
  var onDayClickCallback = (0, _react.useCallback)(function (e) {
    var newDate = new Date(year, month, Number(e.target.innerText));
    updateSelectedDate(newDate, {
      reason: _constants.DATE_PICKER_CHANGE_REASON.DROPDOWN_CLICK
    });
  }, [month, year, updateSelectedDate]);
  var onDayFocusCallback = (0, _react.useCallback)(function (e) {
    var newDate = new Date(year, month, Number(e.target.innerText));
    updateFocusDate(newDate);
  }, [month, year, updateFocusDate]);
  var onDayKeyDownCallback = (0, _react.useCallback)(function (e) {
    var newDate;
    switch (e.keyCode) {
      case _constants.KEY_CODES.ENTER:
      case _constants.KEY_CODES.SPACE:
        newDate = focusDate;
        updateSelectedDate(newDate, {
          reason: _constants.DATE_PICKER_CHANGE_REASON.DROPDOWN_KEYBOARD
        });
        break;
      case _constants.KEY_CODES.ARROW_RIGHT:
        newDate = (0, _dates.getNextAvailableDay)(focusDate, parsedDisabledDates, parsedMinDate, parsedMaxDate);
        if (newDate) {
          updateFocusDate(newDate);
        }
        break;
      case _constants.KEY_CODES.ARROW_LEFT:
        newDate = (0, _dates.getPreviousAvailableDay)(focusDate, parsedDisabledDates, parsedMinDate, parsedMaxDate);
        updateFocusDate(newDate);
        break;
      case _constants.KEY_CODES.ARROW_DOWN:
        newDate = (0, _dateFns.addDays)(focusDate, 7);
        updateFocusDate(newDate);
        break;
      case _constants.KEY_CODES.ARROW_UP:
        newDate = (0, _dateFns.addDays)(focusDate, -7);
        updateFocusDate(newDate);
        break;
      case _constants.KEY_CODES.PAGE_UP:
        newDate = (0, _dateFns.addMonths)(focusDate, -1);
        updateFocusDate(newDate, true, true);
        break;
      case _constants.KEY_CODES.PAGE_DOWN:
        newDate = (0, _dateFns.addMonths)(focusDate, 1);
        updateFocusDate(newDate, true, true);
        break;
      case _constants.KEY_CODES.HOME:
        newDate = (0, _dateFns.addDays)(focusDate, -(0, _dateFns.getDay)(focusDate));
        updateFocusDate(newDate);
        break;
      case _constants.KEY_CODES.END:
        newDate = (0, _dateFns.addDays)(focusDate, 6 - (0, _dateFns.getDay)(focusDate));
        updateFocusDate(newDate);
        break;
      default:
        break;
    }
    if (newDate) {
      e.stopPropagation();
      e.preventDefault();
    }
  }, [focusDate, updateFocusDate, updateSelectedDate, parsedDisabledDates, parsedMinDate, parsedMaxDate]);

  /** *******
   * RENDER *
   ******** */

  var renderDatePickerHeader = /*#__PURE__*/_react.default.createElement(_Flex.Flex, {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center"
  }, /*#__PURE__*/_react.default.createElement(_DatePickerButton.DatePickerButton, {
    "aria-label": "previous month",
    disabled: isPrevMonthDisabled,
    onClick: onPrevMonthCallback
  }, /*#__PURE__*/_react.default.createElement(_index.IconChevronLeft, null)), /*#__PURE__*/_react.default.createElement(_Flex.Flex, {
    display: "flex",
    justifyContent: "center",
    alignItems: "center"
  }, /*#__PURE__*/_react.default.createElement(_Heading.Heading, {
    id: ariaLabelledby.current,
    "aria-live": "polite",
    level: 6
  }, _constants.DATE_PICKER_MONTHS[month], /*#__PURE__*/_react.default.createElement(_VisuallyHidden.VisuallyHidden, null, year)), /*#__PURE__*/_react.default.createElement(_DatePickerSelect.DatePickerSelect, {
    value: year,
    onChange: onYearChangeCallback
  }, yearRange.map(function (value, index) {
    return (
      /*#__PURE__*/
      // eslint-disable-next-line zillow/react/no-array-index-key
      _react.default.createElement("option", {
        key: index
      }, value)
    );
  }))), /*#__PURE__*/_react.default.createElement(_DatePickerButton.DatePickerButton, {
    "aria-label": "next month",
    disabled: isNextMonthDisabled,
    onClick: onNextMonthCallback
  }, /*#__PURE__*/_react.default.createElement(_index.IconChevronRight, null)));
  var renderCalendarHeader = /*#__PURE__*/_react.default.createElement("tr", null, _constants.DATE_PICKER_DAYS.map(function (day, index) {
    return (
      /*#__PURE__*/
      // eslint-disable-next-line zillow/react/no-array-index-key
      _react.default.createElement("th", {
        key: index,
        scope: "col",
        abbr: _constants.DATE_PICKER_DAYS[index]
      }, /*#__PURE__*/_react.default.createElement(_Text.Text, {
        fontType: "bodySmallHeading"
      }, day.slice(0, 1)))
    );
  }));
  var renderCalendarBody = monthMatrix.map(function (week, weekIndex) {
    return (
      /*#__PURE__*/
      // eslint-disable-next-line zillow/react/no-array-index-key
      _react.default.createElement("tr", {
        key: weekIndex
      }, week.map(function (day, dayIndex) {
        if (day === null) {
          // eslint-disable-next-line zillow/react/no-array-index-key
          return /*#__PURE__*/_react.default.createElement("td", {
            key: weekIndex + "_" + dayIndex
          });
        }
        var currentDate = new Date(year, month, day);
        var today = (0, _dateFns.isSameDay)(currentDate, new Date());
        var pressed = (0, _dateFns.isSameDay)(currentDate, parsedSelectedDate);
        var focus = (0, _dateFns.isSameDay)(currentDate, focusDate);
        var disabled = (0, _dates.isDisabledDate)(currentDate, parsedDisabledDates, parsedMinDate, parsedMaxDate);
        return (
          /*#__PURE__*/
          // eslint-disable-next-line zillow/react/no-array-index-key
          _react.default.createElement("td", {
            key: weekIndex + "_" + dayIndex
          }, /*#__PURE__*/_react.default.createElement(_DatePickerButton.DatePickerButton, {
            disabled: disabled,
            onKeyDown: onDayKeyDownCallback,
            onClick: onDayClickCallback,
            onFocus: onDayFocusCallback,
            ref: focus ? forwardedRef : undefined,
            pressed: pressed,
            tabIndex: focus ? '0' : '-1',
            today: today
          }, day))
        );
      }))
    );
  });
  return /*#__PURE__*/_react.default.createElement(_DatePicker.StyledDatePicker, (0, _extends2.default)({
    "aria-labelledby": ariaLabelledby.current
  }, rest), renderDatePickerHeader, /*#__PURE__*/_react.default.createElement("table", {
    role: "grid",
    "aria-labelledby": ariaLabelledby.current
  }, /*#__PURE__*/_react.default.createElement("thead", null, renderCalendarHeader), /*#__PURE__*/_react.default.createElement("tbody", null, renderCalendarBody)));
});
exports.DatePicker = DatePicker;
DatePicker.displayName = 'DatePicker';
DatePicker.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * The format of the date when parsing date strings. This format will also be used for
   * formatting dates returned to the `onChange` callback.
   *
   * Formatting options can be seen in [date-fns](https://date-fns.org/v2.13.0/docs/format).
   */
  dateFormat: _propTypes.default.string,
  /**
   * An array of dates that are unavailable to be selected.
   */
  disabledDates: _propTypes.default.oneOfType([_propTypes.default.arrayOf(_propTypes.default.oneOfType([_propTypes.default.instanceOf(Date), _propTypes.default.string])), _propTypes.default.func]),
  /**
   * The oldest date allowed by the date picker. Defaults to 5 years before the selected date.
   */
  minDate: _propTypes.default.oneOfType([_propTypes.default.instanceOf(Date), _propTypes.default.string]),
  /**
   * The newest date allowed by the date picker. Defaults to 5 years after the selected date.
   */
  maxDate: _propTypes.default.oneOfType([_propTypes.default.instanceOf(Date), _propTypes.default.string]),
  /**
   * Callback function when a day is selected from the calendar using a mouse or keyboard.
   * The callback will receive two objects. One with the date object `date`, and the formatted
   * date string `formatted` as properties. Second, with the meta information.
   */
  onChange: _propTypes.default.func,
  /**
   * The selected date when using this as a
   * [controlled component](https://reactjs.org/docs/forms.html#controlled-components).
   */
  selected: _propTypes.default.oneOfType([_propTypes.default.instanceOf(Date), _propTypes.default.string]),
  /**
   * The default selected date when using this as an
   * [uncontrolled component](https://reactjs.org/docs/uncontrolled-components.html).
   */
  defaultSelected: _propTypes.default.oneOfType([_propTypes.default.instanceOf(Date), _propTypes.default.string])
} : {};
DatePicker.defaultProps = {
  dateFormat: 'MM/dd/yyyy',
  disabledDates: [],
  minDate: undefined,
  maxDate: undefined,
  onChange: undefined,
  selected: undefined,
  defaultSelected: new Date()
};